import {match} from 'ts-pattern';

import type {VariantTechnicalData} from '@dms/api/new-vehicle';

import type {Nullish} from 'shared/web';

import {HYBRID_FUEL} from './catalogueUi';

type VariantTechnicalDataKey = keyof VariantTechnicalData;
export type EngineFuel = {fuel: string; hybridType?: string | Nullish};

const ELECTRIC_FUEL = 'FUELTYPE_ELECTRIC';
const HYDROGEN_FUEL = 'FUELTYPE_HYDROGEN';
const PLUG_IN_HYBRID = 'HYBRIDTYPE_PHEV';

const ELECTRIC_RANGE = ['electricRange', 'electricRangeCity'] as const;
const COMBINED_RANGE = ['combinedRange', 'combinedRangeCity'] as const;
const LITRE_CONSUMPTION = [
  'fuelConsumptionWltpCombined',
  'fuelConsumptionWltpCityCenter',
  'fuelConsumptionWltpOutskirts',
  'fuelConsumptionWltpCountryRoads',
  'fuelConsumptionWltpHighway',
] as const;
const KWH_CONSUMPTION = [
  'fuelConsumptionWltpCombinedKwh',
  'fuelConsumptionWltpCityCenterKwh',
  'fuelConsumptionWltpOutskirtsKwh',
  'fuelConsumptionWltpCountryRoadsKwh',
  'fuelConsumptionWltpHighwayKwh',
] as const;
const BODY = ['dimensions', 'wheelBase', 'operatingWeight', 'trunkSpace'] as const;
/**
 * The technical data a vehicle with this engine keeps; the backend refuses the rest (D85,
 * DMS-NV-CT-0075). Battery and charging belong to electric and hybrid engines, the particulate
 * filter to every engine.
 */
export const getTechnicalDataFit = (engine: EngineFuel) => ({
  hasBattery: engine.fuel === ELECTRIC_FUEL || engine.fuel === HYBRID_FUEL,
  variantKeys: match<typeof engine, readonly VariantTechnicalDataKey[]>(engine)
    .with({fuel: ELECTRIC_FUEL}, () => [
      ...ELECTRIC_RANGE,
      ...KWH_CONSUMPTION,
      'carbonDioxideEmission',
      'emissionClass',
      ...BODY,
    ])
    .with({fuel: HYBRID_FUEL, hybridType: PLUG_IN_HYBRID}, () => [
      ...ELECTRIC_RANGE,
      ...COMBINED_RANGE,
      ...LITRE_CONSUMPTION,
      'fuelConsumptionWltpWeighted',
      ...KWH_CONSUMPTION,
      'fuelConsumptionWltpWeightedCombinedKwh',
      'carbonDioxideEmissionDischarged',
      'carbonDioxideEmissionWeightedCombined',
      'emissionClass',
      ...BODY,
    ])
    .with({fuel: HYBRID_FUEL}, () => [
      ...ELECTRIC_RANGE,
      ...COMBINED_RANGE,
      ...LITRE_CONSUMPTION,
      'carbonDioxideEmission',
      'emissionClass',
      ...BODY,
    ])
    .with({fuel: HYDROGEN_FUEL}, () => [...LITRE_CONSUMPTION, 'emissionClass', ...BODY])
    .otherwise(() => [...LITRE_CONSUMPTION, 'carbonDioxideEmission', 'emissionClass', ...BODY]),
});
